/**
 * Plant Daily P&L — the ERP replacement for the CEO's "Daily Reporting Sheet"
 * workbook (30 daily tabs + Summary, driven by a Monthly Exp master).
 * Backend: routers/plant_pnl.py.
 *
 * Daily P&L    → the day's seven blocks, same order as the workbook
 * Month Summary→ per-day rows + running cumulative (his Summary tab)
 * Fixed Costs  → the monthly fixed-cost master (his Monthly Exp tab)
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, ChevronLeft, ChevronRight, Trash2, Settings2, TrendingUp, TrendingDown,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPatch, apiDel, apiPut } from '../lib/api';
import { formatISTDateTime } from '../lib/format';

const TABS = ['Daily P&L', 'Month Summary', 'Fixed Costs'];
const CATEGORIES = ['Rent', 'EMI', 'Salary', 'Labour Charges', 'Electricity', 'RTO', 'Other'];

const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr0 = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n, d = 2) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Block({ title, right, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="kpi-overline">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function PlantPnL() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Daily P&L');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Daily P&L' ? {} : { tab: t }); };

  return (
    <div data-testid="plant-pnl-page">
      <PageHeader
        overline="Finance & Admin"
        title="Plant Daily P&L"
        subtitle="The day's sale, consumption, overhead and net profit — built from live plant transactions."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`pnl-tab-${t.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Daily P&L' && <DailyTab />}
          {tab === 'Month Summary' && <MonthTab />}
          {tab === 'Fixed Costs' && <FixedCostsTab />}
        </div>
      </div>
    </div>
  );
}

// ── Daily P&L ────────────────────────────────────────────────────────────────
function DailyTab() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [showRes, setShowRes] = useState(false);

  const load = () => apiGet('/plant/daily-pnl', { date })
    .then(setData).catch(() => toast.error('Failed to load daily P&L'));
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftDay = (delta) => {
    const d = new Date(date); d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const delRes = async (id) => {
    try { await apiDel(`/plant/daily-resources/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };

  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  const n = data.net_pnl, p = data.per_cum;
  const profit = n.net >= 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => shiftDay(-1)} className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"><ChevronLeft size={14} /></button>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input w-auto" data-testid="pnl-date" />
        <button onClick={() => shiftDay(1)} className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"><ChevronRight size={14} /></button>
        <button onClick={() => setDate(todayISO())} className="text-xs text-[#1F7A6E] hover:underline ml-1">Today</button>
      </div>

      {/* Net P&L headline */}
      <div className={`border rounded-sm p-4 ${profit ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="kpi-overline flex items-center gap-1">
              {profit ? <TrendingUp size={12} className="text-emerald-700" /> : <TrendingDown size={12} className="text-red-700" />}
              Net Profit / Loss
            </div>
            <div className={`font-display text-3xl font-extrabold tracking-tight ${profit ? 'text-emerald-700' : 'text-red-700'}`} data-testid="pnl-net">
              {inr(n.net)}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <div><div className="text-slate-500 text-xs">Sale</div><div className="font-display font-bold">{inr0(n.sale)}</div></div>
            <div><div className="text-slate-500 text-xs">Raw Material</div><div className="font-display font-bold">− {inr0(n.raw_material)}</div></div>
            <div><div className="text-slate-500 text-xs">Fix Cost</div><div className="font-display font-bold">− {inr0(n.fix_cost)}</div></div>
            <div><div className="text-slate-500 text-xs">Daily Resources</div><div className="font-display font-bold">− {inr0(n.daily_resources)}</div></div>
          </div>
        </div>
      </div>

      {/* Per-m3 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Concrete Sold', `${num(p.qty_m3)} m³`], ['Realisation / m³', inr(p.realisation)],
          ['Production Cost / m³', inr(p.production_cost)], ['Overhead / m³', inr(p.overhead_cost)]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="kpi-overline">{l}</div>
            <div className="font-display text-lg font-extrabold tracking-tight">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Sale */}
        <Block title={`Sale · ${num(data.sale.total_qty_m3)} m³`} right={<span className="font-display font-bold text-sm">{inr(data.sale.total_amount)}</span>}>
          <table className="w-full dense-table">
            <thead><tr><th>Client</th><th>Grade</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.sale.rows.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-slate-400">No sales this day.</td></tr>}
              {data.sale.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.client}{r.jobsite ? <span className="text-slate-400 text-xs"> · {r.jobsite}</span> : ''}</td>
                  <td className="font-display font-bold">{r.grade}</td>
                  <td className="text-right tabular-nums">{num(r.qty_m3)}</td>
                  <td className="text-right tabular-nums">{num(r.rate)}</td>
                  <td className="text-right tabular-nums font-semibold">{num(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>

        {/* Raw material */}
        <Block title={`Raw Material Consumed · ${data.raw_material.batch_count} batch(es)`} right={<span className="font-display font-bold text-sm">{inr(data.raw_material.total_amount)}</span>}>
          <table className="w-full dense-table">
            <thead><tr><th>Material</th><th>UOM</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.raw_material.rows.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-slate-400">No production this day.</td></tr>}
              {data.raw_material.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.material}</td><td className="text-xs text-slate-500">{r.uom}</td>
                  <td className="text-right tabular-nums">{num(r.qty, 0)}</td>
                  <td className="text-right tabular-nums">{num(r.rate)}</td>
                  <td className="text-right tabular-nums font-semibold">{num(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>

        {/* Purchase */}
        <Block title="Purchase / Material Received" right={<span className="font-display font-bold text-sm">{inr(data.purchase.total_amount)}</span>}>
          <table className="w-full dense-table">
            <thead><tr><th>Vendor</th><th>Material</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.purchase.rows.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-slate-400">No material received this day.</td></tr>}
              {data.purchase.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.vendor}</td><td className="text-xs">{r.material}</td>
                  <td className="text-right tabular-nums">{num(r.qty, 0)}</td>
                  <td className="text-right tabular-nums">{num(r.rate)}</td>
                  <td className="text-right tabular-nums font-semibold">{num(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100">
            Purchases are cash-flow, not a cost of this day's concrete — they don't enter the P&L until consumed.
          </div>
        </Block>

        {/* Fix cost */}
        <Block title={`Fix Cost / Day · ${inr0(data.fixed_cost.monthly_total)} ÷ ${data.fixed_cost.days_in_month} days`}
          right={<span className="font-display font-bold text-sm">{inr(data.fixed_cost.per_day_total)}</span>}>
          <table className="w-full dense-table">
            <thead><tr><th>Category</th><th className="text-right">Monthly</th><th className="text-right">Per Day</th></tr></thead>
            <tbody>
              {data.fixed_cost.by_category.length === 0 && <tr><td colSpan="3" className="text-center py-6 text-slate-400">No fixed costs set — add them under the Fixed Costs tab.</td></tr>}
              {data.fixed_cost.by_category.map((c) => (
                <tr key={c.category}>
                  <td>{c.category}</td>
                  <td className="text-right tabular-nums">{num(c.monthly_amount, 0)}</td>
                  <td className="text-right tabular-nums font-semibold">{num(c.per_day_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      </div>

      {/* Daily resources */}
      <Block title="Daily Resources Used"
        right={<div className="flex items-center gap-3">
          <span className="font-display font-bold text-sm">{inr(data.daily_resources.total_amount)}</span>
          <button onClick={() => setShowRes(true)} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1" data-testid="pnl-add-resource"><Plus size={12} /> Add</button>
        </div>}>
        <table className="w-full dense-table">
          <thead><tr><th>Particular</th><th>Remark</th><th className="text-right">Amount</th><th></th></tr></thead>
          <tbody>
            {data.daily_resources.rows.length === 0 && <tr><td colSpan="4" className="text-center py-6 text-slate-400">Nothing logged — add toll, pump charges, food etc. for this day.</td></tr>}
            {data.daily_resources.rows.map((r) => (
              <tr key={r.id}>
                <td>{r.particular}</td>
                <td className="text-xs text-slate-500">{r.remark || '—'}</td>
                <td className="text-right tabular-nums font-semibold">{num(r.amount)}</td>
                <td className="text-right">
                  <button onClick={() => delRes(r.id)} className="text-slate-300 hover:text-red-600" title="Remove"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Block>

      {showRes && <AddResourceModal date={date} onClose={() => setShowRes(false)} onSaved={() => { setShowRes(false); load(); }} />}
    </div>
  );
}

function AddResourceModal({ date, onClose, onSaved }) {
  const [form, setForm] = useState({ particular: 'Toll', amount: '', remark: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.particular.trim()) return toast.error('Particular is required');
    if (!form.amount || Number(form.amount) < 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      await apiPost('/plant/daily-resources', {
        date, particular: form.particular.trim(), amount: Number(form.amount), remark: form.remark || null,
      });
      toast.success('Added');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Daily Resource — ${date}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Particular">
          <input list="res-particulars" value={form.particular} onChange={set('particular')} className="input" autoFocus data-testid="res-particular" />
          <datalist id="res-particulars">
            {['Toll', 'Pump', 'Lunch, Dinner & Other Exp', 'Vehicle Repair', 'Site Expense'].map((o) => <option key={o} value={o} />)}
          </datalist>
        </Field>
        <Field label="Amount (₹)"><input type="number" value={form.amount} onChange={set('amount')} className="input" data-testid="res-amount" /></Field>
        <Field label="Remark"><input value={form.remark} onChange={set('remark')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="res-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Month Summary ────────────────────────────────────────────────────────────
function MonthTab() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [showCfg, setShowCfg] = useState(false);

  const load = () => apiGet('/plant/monthly-pnl', { month })
    .then(setData).catch(() => toast.error('Failed to load month summary'));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  const t = data.totals;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input w-auto" data-testid="pnl-month" />
        <button onClick={() => setShowCfg(true)} className="text-xs text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 ml-auto">
          <Settings2 size={12} /> Opening balance
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Concrete Sold', `${num(t.qty, 0)} m³`], ['Sale', inr0(t.sale)], ['Raw Material', inr0(t.raw)],
          ['Net P&L', inr0(t.net)], ['Closing Cumulative', inr0(data.closing_cumulative)]].map(([l, v], i) => (
          <div key={l} className={`bg-white border rounded-sm p-3 ${i === 3 ? (t.net >= 0 ? 'border-emerald-300' : 'border-red-300') : 'border-slate-200'}`}>
            <div className="kpi-overline">{l}</div>
            <div className={`font-display text-lg font-extrabold tracking-tight ${i === 3 ? (t.net >= 0 ? 'text-emerald-700' : 'text-red-700') : ''}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Date</th><th className="text-right">Qty (m³)</th><th className="text-right">Sale</th>
            <th className="text-right">Raw Material</th><th className="text-right">Fix Cost</th>
            <th className="text-right">Resources</th><th className="text-right">Net</th>
            <th className="text-right">Cumulative</th><th className="text-right">Purchase</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No days recorded yet this month.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.date} data-testid={`pnl-row-${r.date}`}>
                <td className="font-mono text-xs">{r.date.slice(8)}-{r.date.slice(5, 7)}</td>
                <td className="text-right tabular-nums">{num(r.qty_m3, 1)}</td>
                <td className="text-right tabular-nums">{num(r.sale_amount, 0)}</td>
                <td className="text-right tabular-nums">{num(r.raw_material, 0)}</td>
                <td className="text-right tabular-nums text-slate-500">{num(r.fix_cost, 0)}</td>
                <td className="text-right tabular-nums text-slate-500">{num(r.daily_resources, 0)}</td>
                <td className={`text-right tabular-nums font-semibold ${r.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{num(r.net, 0)}</td>
                <td className="text-right tabular-nums text-slate-500">{num(r.cumulative, 0)}</td>
                <td className="text-right tabular-nums text-slate-400">{num(r.purchase_amount, 0)}</td>
              </tr>
            ))}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot><tr className="border-t-2 border-slate-300 font-display font-bold">
              <td>Total</td>
              <td className="text-right tabular-nums">{num(t.qty, 1)}</td>
              <td className="text-right tabular-nums">{num(t.sale, 0)}</td>
              <td className="text-right tabular-nums">{num(t.raw, 0)}</td>
              <td className="text-right tabular-nums">{num(t.fix, 0)}</td>
              <td className="text-right tabular-nums">{num(t.res, 0)}</td>
              <td className={`text-right tabular-nums ${t.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{num(t.net, 0)}</td>
              <td className="text-right tabular-nums">{num(data.closing_cumulative, 0)}</td>
              <td className="text-right tabular-nums">{num(t.purchase, 0)}</td>
            </tr></tfoot>
          )}
        </table>
      </div>
      <div className="text-xs text-slate-500">
        Avg realisation <b>{inr(data.avg_realisation_per_m3)}</b>/m³ · avg production cost <b>{inr(data.avg_production_cost_per_m3)}</b>/m³ ·
        fixed cost <b>{inr(data.fixed_cost_per_day)}</b>/day (÷{data.days_in_month} days)
      </div>

      {showCfg && <OpeningBalanceModal current={data.opening_cumulative} onClose={() => setShowCfg(false)} onSaved={() => { setShowCfg(false); load(); }} />}
    </div>
  );
}

function OpeningBalanceModal({ current, onClose, onSaved }) {
  const [val, setVal] = useState(String(current ?? 0));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/plant/pnl-config', { opening_cumulative: Number(val || 0) });
      toast.success('Opening balance updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Opening Cumulative Balance" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          The carried-forward position the running cumulative starts from. Enter a negative value
          for accumulated loss / capital still to be recovered.
        </p>
        <Field label="Opening Cumulative (₹)"><input type="number" value={val} onChange={(e) => setVal(e.target.value)} className="input" autoFocus /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Fixed Costs master ───────────────────────────────────────────────────────
function FixedCostsTab() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    Promise.all([apiGet('/plant/fixed-costs', { month }), apiGet('/plant/fixed-costs/summary', { month })])
      .then(([l, s]) => { setData(l); setSummary(s); })
      .catch(() => toast.error('Failed to load fixed costs'));
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try { await apiDel(`/plant/fixed-costs/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };

  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input w-auto" data-testid="fc-month" />
        <span className="text-xs text-slate-500">showing costs in force during this month</span>
        <button onClick={() => setShowAdd(true)}
          className="ml-auto px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="fc-add-btn"><Plus size={13} /> Add Fixed Cost</button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="kpi-overline">Monthly Total</div>
            <div className="font-display text-lg font-extrabold">{inr0(summary.monthly_total)}</div>
          </div>
          <div className="bg-white border border-[#1F7A6E] rounded-sm p-3">
            <div className="kpi-overline">Per Day (÷{summary.days_in_month})</div>
            <div className="font-display text-lg font-extrabold text-[#1F7A6E]">{inr(summary.per_day_total)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="kpi-overline">Cost Lines</div>
            <div className="font-display text-lg font-extrabold">{summary.line_count}</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Vendor</th><th>Particular</th><th>Category</th><th className="text-right">Qty</th>
            <th className="text-right">Rate</th><th className="text-right">Monthly</th><th>Effective</th><th></th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-slate-400">No fixed costs for this month — add rent, salaries, EMI etc.</td></tr>}
            {data.items.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setEditing(r)} data-testid={`fc-row-${r.id}`}>
                <td className="font-display font-semibold">{r.vendor}</td>
                <td>{r.particular}</td>
                <td><span className="tag bg-slate-100 text-slate-700">{r.category}</span></td>
                <td className="text-right tabular-nums">{num(r.qty, 0)}</td>
                <td className="text-right tabular-nums">{num(r.rate, 0)}</td>
                <td className="text-right tabular-nums font-semibold">{num(r.amount, 0)}</td>
                <td className="text-xs text-slate-500">{r.effective_from}{r.effective_to ? ` → ${r.effective_to}` : ' →'}</td>
                <td className="text-right">
                  <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} className="text-slate-300 hover:text-red-600" title="Remove"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Costs are effective-dated — closing one off (set an end month) leaves past months' reported profit untouched.
      </p>

      {showAdd && <FixedCostModal month={month} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <FixedCostModal month={month} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FixedCostModal({ month, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor: existing?.vendor || '', particular: existing?.particular || '',
    category: existing?.category || 'Rent', qty: existing?.qty ?? 1, rate: existing?.rate ?? '',
    effective_from: existing?.effective_from || month, effective_to: existing?.effective_to || '',
    remark: existing?.remark || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const amount = Number(form.qty || 0) * Number(form.rate || 0);

  const save = async () => {
    if (!form.vendor.trim()) return toast.error('Vendor is required');
    if (!form.particular.trim()) return toast.error('Particular is required');
    setSaving(true);
    try {
      const body = {
        vendor: form.vendor.trim(), particular: form.particular.trim(), category: form.category,
        qty: Number(form.qty || 0), rate: Number(form.rate || 0),
        effective_from: form.effective_from, effective_to: form.effective_to || null,
        remark: form.remark || null,
      };
      if (existing) await apiPatch(`/plant/fixed-costs/${existing.id}`, body);
      else await apiPost('/plant/fixed-costs', body);
      toast.success(existing ? 'Updated' : 'Added');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={existing ? `Edit — ${existing.vendor}` : 'Add Fixed Cost'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor *"><input value={form.vendor} onChange={set('vendor')} className="input" autoFocus data-testid="fc-vendor" placeholder="ELR Industries Pvt Ltd" /></Field>
          <Field label="Particular *"><input value={form.particular} onChange={set('particular')} className="input" data-testid="fc-particular" placeholder="TM / Land Rent / Accountant" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Category">
            <select value={form.category} onChange={set('category')} className="input" data-testid="fc-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Qty"><input type="number" value={form.qty} onChange={set('qty')} className="input" data-testid="fc-qty" /></Field>
          <Field label="Rate (₹)"><input type="number" value={form.rate} onChange={set('rate')} className="input" data-testid="fc-rate" /></Field>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm flex justify-between">
          <span className="text-slate-500">Monthly amount</span>
          <span className="font-display font-bold">{inr(amount)}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Effective From (month)"><input type="month" value={form.effective_from} onChange={set('effective_from')} className="input" /></Field>
          <Field label="Effective To (blank = ongoing)"><input type="month" value={form.effective_to} onChange={set('effective_to')} className="input" /></Field>
        </div>
        <Field label="Remark"><input value={form.remark} onChange={set('remark')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="fc-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : (existing ? 'Save Changes' : 'Add Fixed Cost')}
        </button>
      </div>
    </ModalShell>
  );
}
